import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { Link } from "react-router";
import { formatarDiaISO } from "@caixaflow/shared";
import { CHAVE_CAIXA_ATUAL } from "../../api/caixa";
import { api, ErroApi } from "../../api/cliente";
import type { CaixaDetalhe, Evento } from "../../api/tipos";
import { Campo, EntradaReais } from "../../components/Campos";
import { Carregando, FalhaAoCarregar, mensagemDeErro } from "../../components/Estados";
import { useToast } from "../../components/Toast";
import { lembrar } from "../../offline/ultimoConhecido";

export function AbrirCaixa() {
  const eventos = useQuery({ queryKey: ["eventos"], queryFn: () => api<Evento[]>("/eventos") });
  const emAndamento = eventos.data?.filter((e) => e.status === "EM_ANDAMENTO") ?? [];
  const [eventoId, setEventoId] = useState("");
  const [fundo, setFundo] = useState<number | null>(null);
  const [observacoes, setObservacoes] = useState("");
  const [erroLocal, setErroLocal] = useState<string | null>(null);
  const qc = useQueryClient();
  const toast = useToast();

  const escolhido = eventoId || emAndamento[0]?.id || "";
  const abrir = useMutation({
    mutationFn: () =>
      api<CaixaDetalhe>("/caixas", "POST", {
        eventoId: escolhido,
        fundoTrocoCentavos: fundo,
        observacoes: observacoes.trim() || undefined,
      }),
    onSuccess: (caixa) => {
      qc.setQueryData(CHAVE_CAIXA_ATUAL, caixa);
      lembrar("caixa-atual", caixa);
      toast(`Caixa #${caixa.numero} aberto`);
    },
  });
  const erro = abrir.error instanceof ErroApi ? abrir.error : null;

  if (eventos.isPending) return <Carregando />;
  if (eventos.error) return <FalhaAoCarregar erro={eventos.error} tentarDeNovo={() => eventos.refetch()} />;
  if (emAndamento.length === 0) {
    return (
      <div className="card vazio">
        Nenhum evento em andamento. Peça a um ADMIN para iniciar o evento antes de abrir o caixa.
      </div>
    );
  }

  const enviar = (e: FormEvent) => {
    e.preventDefault();
    if (fundo === null) {
      setErroLocal("Informe o fundo de troco (0,00 se não houver dinheiro inicial).");
      return;
    }
    setErroLocal(null);
    abrir.mutate();
  };

  return (
    <>
      <form className="card pad form" onSubmit={enviar} style={{ maxWidth: 520 }}>
        <h3>Abrir caixa</h3>
        {(erroLocal || (abrir.error && !erro?.campos.length)) && (
          <div className="aviso erro" role="alert">
            {erroLocal ?? mensagemDeErro(abrir.error)}
          </div>
        )}
        <Campo rotulo="Evento">
          <select value={escolhido} onChange={(e) => setEventoId(e.target.value)}>
            {emAndamento.map((ev) => (
              <option key={ev.id} value={ev.id}>
                {ev.nome} · {formatarDiaISO(ev.dataInicio)}
              </option>
            ))}
          </select>
        </Campo>
        <Campo rotulo="Fundo de troco (R$)" dica="dinheiro que já está na gaveta" erro={erro?.doCampo("fundoTrocoCentavos")}>
          <EntradaReais centavos={fundo} onChange={setFundo} />
        </Campo>
        <Campo rotulo="Observações" dica="opcional">
          <textarea value={observacoes} onChange={(e) => setObservacoes(e.target.value)} maxLength={500} />
        </Campo>
        <button className="btn primario largo" type="submit" disabled={abrir.isPending}>
          {abrir.isPending ? "Abrindo…" : "Abrir caixa"}
        </button>
        <p className="mudo pequeno" style={{ margin: 0 }}>
          A data e a hora de abertura são registradas automaticamente.
        </p>
      </form>
      <Link to="/fechamento" className="pequeno">
        Ver meus caixas anteriores
      </Link>
    </>
  );
}
